import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, map, Observable, throwError } from 'rxjs';

// HTTP options for application/x-www-form-urlencoded
const HTTP_OPTIONS = {
  headers: new HttpHeaders({
    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
  }),
  withCredentials: true
};

// ============================================
// INTERFACES
// ============================================

export interface BaseDataInfo {
  CODE_KEY?: number;
  CODE_DESCRIPTION?: string;
  CODE_GROUP?: string;
}

// Label types supported by this component
export type BaseInfoLabel = 'Base Basket' | 'Base Type' | 'Base FDII';

// ============================================
// SERVICE
// ============================================

@Injectable()
export class ExpenseTransInfoService {

  // Action codes for each label type
  private actionCodes: Record<BaseInfoLabel, string> = {
    'Base Basket': 'z6htpi',
    'Base Type': '6kzmd7',
    'Base FDII': 'a8rd93'
  };

  // Global params from AngularJS
  private globalParams: any = {};
  // Filter params from AngularJS workspace filters
  private filterParams: any = {};

  constructor(private http: HttpClient) {}

  // ============================================
  // SET GLOBAL/FILTER PARAMS
  // ============================================
  
  setGlobalParams(globalParams: any): void {
    this.globalParams = globalParams || {};
  }

  setFilterParams(filterParams: any): void {
    this.filterParams = filterParams || {};
  }

  // ============================================
  // API METHOD
  // ============================================

  /**
   * Get Base Data Info based on label type
   * - Base Basket: action_code = z6htpi
   * - Base Type: action_code = 6kzmd7
   * - Base FDII: action_code = a8rd93
   */
  getBaseDataInfo(
    baseUrl: string, 
    label: BaseInfoLabel, 
    transCatgKey: number,
    ssoId: string,
    taxYear: number,
    scenario: number
  ): Observable<BaseDataInfo[]> {
    const actionCode = this.actionCodes[label];
    
    if (!actionCode) {
      console.error('Unknown label:', label);
      return throwError(() => new Error(`Unknown label: ${label}`));
    }

    const url = `${baseUrl}/loadJsonObject?action_code=${actionCode}`;
    const body = this.buildFormData({
      sso_id: ssoId,
      tax_year: taxYear.toString(),
      scenario: scenario.toString(),
      trans_catg_key: transCatgKey.toString()
    });

    return this.http.post<any>(url, body, HTTP_OPTIONS).pipe(
      map((res: any) => {
        if (res.callSuccess === '1') {
          return res.jsonObject || [];
        }
        return [];
      }),
      catchError(error => {
        console.error('Error fetching base data info:', error);
        return throwError(() => error);
      })
    );
  }

  // ============================================
  // HELPER METHODS
  // ============================================

  private buildFormData(params: { [key: string]: string }): HttpParams {
    const mergedParams: { [key: string]: string } = {};
    
    // Add globalParams
    if (this.globalParams) {
      Object.keys(this.globalParams).forEach(key => {
        const value = this.globalParams[key];
        if (value !== undefined && value !== null && value !== '') {
          mergedParams[key] = String(value);
        }
      });
    }
    
    // Add filterParams
    if (this.filterParams) {
      Object.keys(this.filterParams).forEach(key => {
        const value = this.filterParams[key];
        if (value !== undefined && value !== null && value !== '') {
          mergedParams[key] = String(value);
        }
      });
    }
    
    // Add specific params
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        mergedParams[key] = params[key];
      }
    });
    
    // Build HttpParams
    let httpParams = new HttpParams();
    Object.keys(mergedParams).forEach(key => {
      httpParams = httpParams.set(key, mergedParams[key]);
    });
    
    return httpParams;
  }
}
